export type RetryOptions = {
  maxAttempts?: number;
  backoffMs?: (attempt: number) => number;
  shouldRetry?: (error: unknown) => boolean;
  sleep?: (ms: number) => Promise<void>;
};

const DEFAULT_BACKOFF = (attempt: number) => 1000 * attempt;

export function isTransientFetchError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message ?? "";
  return msg.includes("Failed to fetch") || msg.includes("Load failed") || msg.includes("NetworkError");
}

export async function fetchWithRetry(
  input: Request | string | URL,
  init: RequestInit | undefined,
  options: RetryOptions = {}
): Promise<Response> {
  const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
  const backoff = options.backoffMs ?? DEFAULT_BACKOFF;
  const shouldRetry = options.shouldRetry ?? isTransientFetchError;
  const sleep = options.sleep ?? defaultSleep;

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fetch(input, init);
    } catch (error) {
      lastError = error;
      if (attempt === maxAttempts || !shouldRetry(error)) {
        throw error;
      }
      await sleep(backoff(attempt));
    }
  }
  throw lastError;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
